import { useEffect } from 'react';

const SITE_NAME = 'Canadian Truck Sales';
const DEFAULT_TITLE = 'Canadian Truck Sales | #1 Used Trucks & Trailers Dealer in Mississauga, ON';
const DEFAULT_DESC = 'Canadian Truck Sales (CTS) - Mississauga\'s #1 used truck and trailer dealership. Premium Volvo, Freightliner, Kenworth, Peterbilt, International & Western Star trucks. Reefer, dry van & flatbed trailers. Best prices in the GTA. Financing available. Call (416) 417-2525.';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png';
const BASE_URL = 'https://canadiantrucksales.ca';

// Comprehensive keywords targeting all competitor search terms
const MASTER_KEYWORDS = [
  // Primary brand keywords
  'Canadian Truck Sales', 'CTS trucks', 'CTS Mississauga',
  // Direct competitor targeting keywords (search terms people use to find competitors)
  'used trucks Mississauga', 'used trucks Brampton', 'used trucks GTA', 'used trucks Toronto',
  'used trucks Ontario', 'used trucks for sale Ontario', 'truck dealer Mississauga',
  'truck dealer Brampton', 'truck dealer GTA', 'truck sales Mississauga',
  'used truck dealership near me', 'used truck dealer Ontario Canada',
  // Make-specific (targeting Vision Volvo, Brandt Peterbilt, Kenworth)
  'used Volvo trucks Ontario', 'used Volvo VNL for sale', 'used Volvo 760 Ontario',
  'used Volvo trucks Mississauga', 'used Volvo trucks GTA',
  'used Freightliner trucks Ontario', 'used Freightliner Cascadia', 'used Freightliner for sale GTA',
  'used Kenworth trucks Ontario', 'used Kenworth T680', 'used Kenworth W900',
  'used Peterbilt trucks Ontario', 'used Peterbilt 579', 'used Peterbilt 389',
  'used International trucks Ontario', 'used International LT', 'used International ProStar',
  'used Western Star trucks Ontario', 'used Western Star 5700',
  'used Mack trucks Ontario', 'used Mack Anthem',
  // Vehicle type keywords
  'used day cab trucks Ontario', 'used sleeper trucks Ontario', 'used highway trucks Ontario',
  'used tandem trucks Ontario', 'used heavy duty trucks Ontario',
  'used semi trucks for sale Canada', 'used semi trucks Ontario',
  'used class 8 trucks Ontario', 'used class 8 trucks Canada',
  // Trailer keywords
  'used trailers Ontario', 'used trailers for sale Ontario', 'used trailers Mississauga',
  'used reefer trailers Ontario', 'used refrigerated trailers Ontario',
  'used dry van trailers Ontario', 'used flatbed trailers Ontario',
  'used dump trailers Ontario', 'used utility trailers GTA',
  'used Hyundai trailers', 'used Utility trailers', 'used Wabash trailers', 'used Great Dane trailers',
  // Commercial/fleet keywords
  'commercial trucks for sale Ontario', 'fleet trucks Ontario', 'owner operator trucks Ontario',
  'truck financing Ontario', 'truck financing Mississauga', 'truck financing GTA',
  'buy used truck Ontario', 'best used truck dealer Ontario',
  'affordable used trucks Ontario', 'cheap used trucks GTA',
  'low mileage trucks Ontario', 'certified used trucks Ontario',
  // Location-specific long-tail
  'used trucks Etobicoke', 'used trucks Scarborough', 'used trucks Hamilton',
  'used trucks Kitchener', 'used trucks London Ontario', 'used trucks Ottawa',
  'used trucks Vaughan', 'used trucks Markham', 'used trucks Ajax',
  'used trucks Oshawa', 'used trucks Barrie', 'used trucks Cambridge Ontario',
  'Shawson Drive truck dealer', 'trucks for sale near 401',
  // Transactional intent
  'buy used truck Canada', 'best price used trucks Ontario',
  'truck dealer with financing Ontario', 'no credit truck financing Ontario',
  'truck lease Ontario', 'truck trade in Ontario',
].join(', ');

function setMeta(name, content, attr = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export function SEO({ title, description, image, url, type = 'website', noindex = false, jsonLd, keywords }) {
  const fullTitle = title ? title + ' | ' + SITE_NAME : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const img = image || DEFAULT_IMAGE;
  const canonical = url ? BASE_URL + url : BASE_URL;
  const robots = noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';
  const kw = keywords || MASTER_KEYWORDS;

  useEffect(() => {
    document.title = fullTitle;
    setMeta('description', desc);
    setMeta('keywords', kw);
    setMeta('robots', robots);
    setMeta('googlebot', 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1');
    setLink('canonical', canonical);

    // Open Graph
    setMeta('og:site_name', SITE_NAME, 'property');
    setMeta('og:title', fullTitle, 'property');
    setMeta('og:description', desc, 'property');
    setMeta('og:image', img, 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('og:type', type, 'property');
    setMeta('og:locale', 'en_CA', 'property');

    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', img);

    // Geo
    setMeta('geo.region', 'CA-ON');
    setMeta('geo.placename', 'Mississauga');

    // JSON-LD
    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : [jsonLd];
      schemas.forEach((ld, i) => setJsonLd('seo-jsonld-' + i, ld));
      for (let i = schemas.length; i < 10; i++) removeJsonLd('seo-jsonld-' + i);
    }

    return () => {
      document.title = DEFAULT_TITLE;
      for (let i = 0; i < 10; i++) removeJsonLd('seo-jsonld-' + i);
    };
  }, [fullTitle, desc, img, canonical, robots, type, jsonLd, kw]);

  return null;
}

// ==================== STRUCTURED DATA SCHEMAS ====================

export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "AutoDealer",
  "name": "Canadian Truck Sales",
  "alternateName": ["CTS", "Canadian Truck Sales Inc", "CTS Trucks", "CTS Mississauga"],
  "url": BASE_URL,
  "logo": DEFAULT_IMAGE,
  "image": DEFAULT_IMAGE,
  "description": "Canadian Truck Sales is the premier used truck and trailer dealership in Mississauga, Ontario. We offer a wide selection of premium used Volvo, Freightliner, Kenworth, Peterbilt, International, and Western Star trucks, along with reefer, dry van, and flatbed trailers. Competitive financing available for all credit types.",
  "telephone": "+1-416-417-2525",
  "email": "info@canadiantrucksales.ca",
  "foundingDate": "2005",
  "numberOfEmployees": { "@type": "QuantitativeValue", "minValue": 10, "maxValue": 50 },
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "5939 Shawson Dr Suite #100",
    "addressLocality": "Mississauga",
    "addressRegion": "ON",
    "postalCode": "L4W 3Y2",
    "addressCountry": "CA"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 43.6205,
    "longitude": -79.6441
  },
  "hasMap": "https://maps.google.com/?q=5939+Shawson+Dr+Suite+100+Mississauga+ON+L4W+3Y2",
  "openingHoursSpecification": [
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "09:00", "closes": "18:00" },
    { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "10:00", "closes": "17:00" }
  ],
  "priceRange": "$$",
  "paymentAccepted": ["Cash", "Credit Card", "Debit Card", "Wire Transfer", "Financing"],
  "currenciesAccepted": "CAD, USD",
  "areaServed": [
    { "@type": "City", "name": "Mississauga" },
    { "@type": "City", "name": "Brampton" },
    { "@type": "City", "name": "Toronto" },
    { "@type": "City", "name": "Hamilton" },
    { "@type": "City", "name": "Kitchener" },
    { "@type": "City", "name": "London" },
    { "@type": "AdministrativeArea", "name": "Ontario" },
    { "@type": "Country", "name": "Canada" },
    { "@type": "Country", "name": "United States" }
  ],
  "makesOffer": [
    { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Used Volvo Trucks" } },
    { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Used Freightliner Trucks" } },
    { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Used Kenworth Trucks" } },
    { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Used Peterbilt Trucks" } },
    { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Used International Trucks" } },
    { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Used Western Star Trucks" } },
    { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Used Mack Trucks" } },
    { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Used Reefer Trailers" } },
    { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Used Dry Van Trailers" } },
    { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Used Flatbed Trailers" } }
  ],
  "sameAs": [],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "127",
    "bestRating": "5",
    "worstRating": "1"
  }
};

export const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Where is Canadian Truck Sales located?",
      "acceptedAnswer": { "@type": "Answer", "text": "Canadian Truck Sales is located at 5939 Shawson Dr Suite #100, Mississauga, ON L4W 3Y2. We are conveniently located near Highway 401 and 403, serving the Greater Toronto Area including Brampton, Etobicoke, Toronto, Hamilton, and all of Ontario." }
    },
    {
      "@type": "Question",
      "name": "What brands of used trucks does Canadian Truck Sales carry?",
      "acceptedAnswer": { "@type": "Answer", "text": "We carry all major brands including Volvo (VNL, VNR, 760), Freightliner (Cascadia, Columbia), Kenworth (T680, T880, W900), Peterbilt (579, 389, 567), International (LT, ProStar, LoneStar), Western Star (5700, 4900), and Mack (Anthem, Pinnacle). Our inventory includes both day cabs and sleeper trucks." }
    },
    {
      "@type": "Question",
      "name": "Does Canadian Truck Sales offer financing?",
      "acceptedAnswer": { "@type": "Answer", "text": "Yes! We offer competitive financing options for all credit types. Whether you're a first-time buyer, owner-operator, or fleet manager, our financing team can help you get approved quickly. Apply online or call (416) 417-2525." }
    },
    {
      "@type": "Question",
      "name": "What types of trailers does CTS sell?",
      "acceptedAnswer": { "@type": "Answer", "text": "Canadian Truck Sales offers a wide selection of used trailers including reefer/refrigerated trailers (Carrier, Thermo King), dry van trailers, flatbed trailers, and dump trailers from top manufacturers like Hyundai, Utility, Wabash, Great Dane, and Stoughton." }
    },
    {
      "@type": "Question",
      "name": "Can I ship a truck from Canadian Truck Sales to the USA?",
      "acceptedAnswer": { "@type": "Answer", "text": "Yes, Canadian Truck Sales ships trucks and trailers across Canada and to the United States. We handle all export documentation and can arrange transportation to your location." }
    },
    {
      "@type": "Question",
      "name": "How does CTS compare to other truck dealers in Mississauga?",
      "acceptedAnswer": { "@type": "Answer", "text": "Canadian Truck Sales stands out with our 20+ years of experience, 500+ units sold, 98% client satisfaction rate, and competitive pricing. Located on Shawson Drive in Mississauga, we offer the largest selection of premium used trucks and trailers in the GTA with transparent pricing and fast financing approvals." }
    }
  ]
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Canadian Truck Sales",
  "alternateName": "CTS",
  "url": BASE_URL,
  "potentialAction": {
    "@type": "SearchAction",
    "target": BASE_URL + "/inventory?search={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

export function buildVehicleSchema(item) {
  if (!item) return null;
  const makeMap = {
    'Volvo': 'Volvo Trucks', 'Freightliner': 'Freightliner Trucks', 'Kenworth': 'Kenworth',
    'Peterbilt': 'Peterbilt', 'International': 'International Trucks', 'Western Star': 'Western Star Trucks',
    'Mack': 'Mack Trucks', 'Hyundai': 'Hyundai Translead', 'Utility': 'Utility Trailer',
    'Wabash': 'Wabash National', 'Great Dane': 'Great Dane Trailers'
  };
  return {
    "@context": "https://schema.org",
    "@type": "Vehicle",
    "name": item.title || (item.year + ' ' + item.make + ' ' + item.model),
    "brand": { "@type": "Brand", "name": makeMap[item.make] || item.make },
    "manufacturer": { "@type": "Organization", "name": makeMap[item.make] || item.make },
    "model": item.model,
    "vehicleModelDate": String(item.year || ''),
    "mileageFromOdometer": item.mileage_km ? { "@type": "QuantitativeValue", "value": item.mileage_km, "unitCode": "KMT" } : undefined,
    "vehicleTransmission": item.transmission || undefined,
    "vehicleEngine": item.engine ? { "@type": "EngineSpecification", "name": item.engine } : undefined,
    "vin": item.vin || undefined,
    "color": item.color || undefined,
    "bodyType": item.body_type || item.category || undefined,
    "driveWheelConfiguration": item.drive_type || undefined,
    "fuelType": item.fuel_type || 'Diesel',
    "itemCondition": item.condition === 'New' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
    "vehicleConfiguration": item.category === 'Truck' ? 'Semi-Truck' : 'Trailer',
    "offers": {
      "@type": "Offer",
      "price": item.price || undefined,
      "priceCurrency": "CAD",
      "availability": item.status === 'Sold' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      "url": BASE_URL + '/inventory/' + item.id,
      "seller": {
        "@type": "AutoDealer",
        "name": "Canadian Truck Sales",
        "telephone": "+1-416-417-2525",
        "address": { "@type": "PostalAddress", "addressLocality": "Mississauga", "addressRegion": "ON", "addressCountry": "CA" }
      },
      "itemCondition": item.condition === 'New' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
      "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    },
    "image": item.images && item.images.length > 0 ? item.images.map(function(i) { return i.startsWith('http') ? i : BASE_URL + i; }) : [DEFAULT_IMAGE],
    "url": BASE_URL + '/inventory/' + item.id,
    "description": item.description || ('Premium ' + (item.condition || 'used') + ' ' + (item.year || '') + ' ' + (item.make || '') + ' ' + (item.model || '') + ' for sale at Canadian Truck Sales, Mississauga ON. Financing available. Call (416) 417-2525.')
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map(function(item, i) {
      return {
        "@type": "ListItem",
        "position": i + 1,
        "name": item.name,
        "item": BASE_URL + item.url
      };
    })
  };
}

export function buildInventoryListSchema(items) {
  if (!items || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Used Trucks and Trailers for Sale at Canadian Truck Sales",
    "description": "Browse our current inventory of premium used trucks and trailers in Mississauga, Ontario",
    "numberOfItems": items.length,
    "itemListElement": items.slice(0, 30).map(function(item, i) {
      return {
        "@type": "ListItem",
        "position": i + 1,
        "name": item.title || (item.year + ' ' + item.make + ' ' + item.model),
        "url": BASE_URL + '/inventory/' + item.id
      };
    })
  };
}
